'use client';

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';

import { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { useAppContext } from '@/layouts/AppProvider';

const CheckoutExperienceModal = dynamic(() => import('./Modal'), {
  ssr: false,
  loading: () => null,
});

// Custom event name for triggering the checkout experience modal globally
export const SHOW_CHECKOUT_MODAL_EVENT = 'showChooseYourCheckoutExperienceModal';

// Helper function to trigger the modal from anywhere (including isolated contexts like toast)
export const triggerCheckoutExperienceModal = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SHOW_CHECKOUT_MODAL_EVENT));
  }
};

const MOBILE_BREAKPOINT = 768;

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;

const ChooseYourCheckoutExperience = () => {
  const { showChooseYourCheckoutFlowModal, setShowChooseYourCheckoutFlowModal } = useShoppingCartContext();
  const router = useRouter();
  const { webalias, generateURL: generateApproperiateURL } = useWebAlias();
  const { isBackOffice } = useAppContext();

  // Track mobile state to conditionally skip modal rendering entirely on mobile
  // This prevents any flash of the modal before redirect
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(isMobileViewport());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const navigateToCheckoutExperiencePage = useCallback(() => {
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();

    if (currentParams.get('isGuestUser') === 'true') {
      newParams.set('isGuestUser', 'true');
    }
    const customerId = currentParams.get('customerId');
    if (customerId) {
      newParams.set('customerId', customerId);
    }

    const paramString = newParams.toString();
    const path = `/checkoutexperience${paramString ? '?' + paramString : ''}`;
    router.push(generateApproperiateURL(path, isBackOffice ? 'shopping' : webalias));
  }, [router, generateApproperiateURL, isBackOffice, webalias]);

  // Listen for global event to show modal (used by components in isolated contexts like toast)
  // On mobile, navigate to the checkout experience page instead of showing the modal
  useEffect(() => {
    const handleShowModal = () => {
      if (isMobileViewport()) {
        navigateToCheckoutExperiencePage();
      } else {
        setShowChooseYourCheckoutFlowModal(true);
      }
    };

    window.addEventListener(SHOW_CHECKOUT_MODAL_EVENT, handleShowModal);
    return () => {
      window.removeEventListener(SHOW_CHECKOUT_MODAL_EVENT, handleShowModal);
    };
  }, [setShowChooseYourCheckoutFlowModal, navigateToCheckoutExperiencePage]);

  // Intercept direct state changes (from OrderSummary, CheckoutBtn, CartDropdown)
  // On mobile, redirect to the checkout experience page instead of showing the modal
  // useLayoutEffect runs synchronously before the browser paints, preventing any visual flash
  useLayoutEffect(() => {
    if (showChooseYourCheckoutFlowModal && isMobileViewport()) {
      setShowChooseYourCheckoutFlowModal(false);
      navigateToCheckoutExperiencePage();
    }
  }, [showChooseYourCheckoutFlowModal, setShowChooseYourCheckoutFlowModal, navigateToCheckoutExperiencePage]);

  // On mobile, don't render the modal at all — this is the primary guard against any flash.
  // The useLayoutEffect above handles the redirect, and by not rendering the Modal component,
  // we ensure isOpen={true} never reaches the Modal even for a single frame.
  if (isMobile) {
    return null;
  }

  // On desktop/tablet, render modal as before with its own visibility and close animation
  return <CheckoutExperienceModal />;
};

export default ChooseYourCheckoutExperience;
